import * as React from "react"
import {Form} from "semantic-ui-react"
import {
  BookerFragFragment,
  BookingGutsFragment,
  BookingPaidBy,
  ResendBookingEmail,
  UncancelBooking,
} from "../graphql/queries"
import * as moment from "moment"
import {bookingTimeTooClose} from "../model/time"
import {WithModalStore} from "../App"
import {CancelBookingModal} from "./CancelBookingModal"
import {errorUnionToEither} from "../graphql/errors"
import {GraphQL} from "../graphql"
import {onBookingSetHidden, onInvoiceBookingMutation} from "./BookingWizard"
import {ID} from "../model/id"
import {nullableToLeft} from "../tsutil"
import {isBookingPayer} from "./UpdateBookingWizard"

interface Props extends WithModalStore {
  booking: BookingGutsFragment
  booker: BookerFragFragment
  disabled: boolean
}

export function BookingButtons(props: Props) {
  const now = moment()
  const {booker, booking, disabled, modalStore} = props
  const {isAdmin} = booker
  const {isHidden, isComplete, isCancelled} = booking
  const isInvoiced = booking.invoice !== null
  const isPriced = booking.price !== null && booking.price.amount > 0
  const isBooker = booking.booker && booking.booker.id === booker.id
  const isTooClose = bookingTimeTooClose(booking.status!, now)
  // We only need to check booking.extraCharges here and not booking.automaticExtraCharges because
  // "booking.automaticExtraCharges.length > 0" implies "!isInvoiced", because automaticExtraCharges
  // are only added for non-complete bookings.
  const canInvoice = !isInvoiced || booking.extraCharges.some(({invoice}) => invoice === null)

  const showInvoice =
    isAdmin && isPriced && (isComplete || isCancelled) && canInvoice && !isHidden && booking.booker
  const disabledInvoiceButton =
    booking.paidBy === BookingPaidBy.not_specified && !booking.stripeCard
  const showHide = isAdmin && !isHidden && isComplete && !isInvoiced
  const showUnhide = isAdmin && isHidden
  const showCancel =
    ((isAdmin && !isInvoiced) || (isBooker && !isTooClose)) && !isCancelled && !isHidden
  const showUncancel = isAdmin && !isInvoiced && isCancelled
  const isPayer = isBookingPayer(booking, booker)
  const showResend = (isAdmin || isBooker || isPayer) && booking.booker

  function onBookingSetHiddenModal(ids: Array<ID>, hidden: boolean) {
    const message = "Setting booking " + (hidden ? "hidden" : "unhidden")
    return modalStore.applyModal({
      pending: [`${message}`, `${message}...`],
      error: (m) => [`${message} failed`, m],
      action: async () => {
        return await onBookingSetHidden(ids, hidden)
      },
    })
  }

  async function onUncancelBooking() {
    await modalStore.applyModal({
      pending: ["Un-cancelling booking", "Un-cancelling booking..."],
      error: (m) => ["Un-cancelling booking failed", m],
      action: async () => {
        const data = await GraphQL.mutate(UncancelBooking, {id: booking.id})
        return errorUnionToEither(data.uncancelBooking)
      },
    })
  }

  async function onResendBookingEmail() {
    await modalStore.applyModal({
      pending: ["Resending booking email", "Sending..."],
      error: (m) => ["Resend booking email failed", m],
      action: async () => {
        const data = await GraphQL.mutate(ResendBookingEmail, {id: booking.id})
        return nullableToLeft(data.resendBookingEmail)
      },
    })
  }

  function onCancelBooking() {
    modalStore.showModal(CancelBookingModal, {
      bookingStore: null,
      modalStore,
      id: booking.id,
      message: "Are you sure you want to cancel this booking?",
    })
  }

  async function onInvoiceBooking() {
    await modalStore.applyModal({
      pending: ["Invoicing booking", "Invoicing booking.."],
      error: (m) => ["Invoicing booking failed", m],
      action: async () => {
        return await onInvoiceBookingMutation([booking.id])
      },
    })
  }

  return (
    <Form>
      <Form.Group>
        {showInvoice && (
          <Form.Button
            color="blue"
            disabled={disabled || disabledInvoiceButton}
            onClick={(e) => {
              e.preventDefault()
              modalStore.handlePromiseThrow(onInvoiceBooking())
            }}
            content="Invoice"
          />
        )}
        {showUnhide && (
          <Form.Button
            disabled={disabled}
            onClick={(e) => {
              e.preventDefault()
              modalStore.handlePromiseThrow(onBookingSetHiddenModal([booking.id], false))
            }}
            content="Unhide"
          />
        )}
        {showHide && (
          <Form.Button
            disabled={disabled}
            onClick={(e) => {
              e.preventDefault()
              modalStore.handlePromiseThrow(onBookingSetHiddenModal([booking.id], true))
            }}
            content="Hide"
          />
        )}
        {showResend && (
          <Form.Button
            disabled={disabled}
            onClick={(e) => {
              e.preventDefault()
              modalStore.handlePromiseThrow(onResendBookingEmail())
            }}
            content="Resend booking email"
          />
        )}
        {showCancel && (
          <Form.Button
            disabled={disabled}
            onClick={(e) => {
              e.preventDefault()
              onCancelBooking()
            }}
            content="Cancel"
          />
        )}
        {showUncancel && (
          <Form.Button
            disabled={disabled}
            onClick={(e) => {
              e.preventDefault()
              modalStore.handlePromiseThrow(onUncancelBooking())
            }}
            content="Uncancel"
          />
        )}
      </Form.Group>
    </Form>
  )
}



// WEBPACK FOOTER //
// ./src/components/BookingButtons.tsx